import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  Code2,
  Coins,
  Globe2,
  Link2,
  Lock,
  Megaphone,
  Moon,
  QrCode,
  ShieldCheck,
  Smartphone,
  Share2,
  Sparkles,
  Sun,
  TrendingUp,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/auth/AuthProvider';
import { useTheme } from '@/lib/theme';
import { RequestDemoDialog } from '@/components/RequestDemoDialog';

export default function Landing() {
  const { authenticated, ready, login } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoSource, setDemoSource] = useState<string>('landing');

  const openDemo = (source: string) => {
    setDemoSource(source);
    setDemoOpen(true);
  };

  // Signed-in visitors get a direct path back into the app; we still show the
  // demo CTA as the primary action for everyone else.
  const openApp = () => navigate('/dashboard');

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <Activity className="size-5 text-primary" aria-hidden />
            <span className="tracking-tight">Pulse</span>
          </Link>
          <nav className="flex items-center gap-1.5 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun /> : <Moon />}
            </Button>
            {ready && authenticated ? (
              <Button size="sm" onClick={openApp}>
                Open dashboard
                <ArrowRight />
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={() => login()}>
                  Sign in
                </Button>
                <Button size="sm" onClick={() => openDemo('header')}>
                  Request a demo
                  <ArrowRight />
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* ----- HERO ----- */}
      <section className="border-b">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border bg-muted/40 px-3 py-1 text-xs text-muted-foreground">
              <Sparkles className="size-3 text-primary" aria-hidden />
              The link between mobile apps and the people who promote them
            </div>
            <h1 className="text-4xl font-semibold tracking-tight md:text-6xl">
              <span className="block">Apps create campaigns.</span>
              <span className="block">Creators share.</span>
              <span className="block">Both get paid for real installs.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
              A two-sided attribution platform — pay per matched install or
              signup, with per-user signal on channels paid networks can't
              see.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              {ready && authenticated ? (
                <Button size="lg" onClick={openApp}>
                  Open dashboard
                  <ArrowRight />
                </Button>
              ) : (
                <Button size="lg" onClick={() => openDemo('hero')}>
                  Request a demo
                  <ArrowRight />
                </Button>
              )}
              <Button size="lg" variant="outline" asChild>
                <a href="#how">See how it works</a>
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Free while in beta. No card required.
            </p>
          </div>
        </div>
      </section>

      {/* ----- TWO SIDES ----- */}
      <section id="how" className="border-b">
        <div className="mx-auto grid max-w-6xl gap-6 px-6 py-16 md:grid-cols-2">
          <SideCard
            icon={<Megaphone className="size-5" aria-hidden />}
            audience="For mobile apps"
            title="Reach the right audience without paying for noise"
            bullets={[
              'Set a per-install or per-signup bounty and a budget.',
              'Open to the whole network, or invite specific creators.',
              'Pay only for matched users — settled weekly.',
            ]}
            ctaLabel="Request a demo"
            onCta={() => openDemo('apps-card')}
            visual={<AppPreview />}
          />
          <SideCard
            icon={<Coins className="size-5" aria-hidden />}
            audience="For creators"
            title="Turn your audience into ongoing revenue"
            bullets={[
              'Browse campaigns from real apps and pick the fit.',
              'Personal link, QR, and ready-to-paste snippets — minted on claim.',
              'Cash out once you cross the payout threshold.',
            ]}
            ctaLabel="Request a demo"
            onCta={() => openDemo('creators-card')}
            visual={<CreatorPreview />}
          />
        </div>
      </section>

      {/* ----- PROBLEM ----- */}
      <section className="border-b bg-muted/20">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Why mobile attribution is broken
            </h2>
            <p className="mt-4 text-muted-foreground">
              The mobile browser and the app live in separate OS sandboxes,
              so cookies can't bridge a click on Instagram to an install
              from the App Store. Every mobile-app team hits this wall.
            </p>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <ProblemCard
              icon={<Lock className="size-5" aria-hidden />}
              title="Cookies can't cross the sandbox"
              body="A click in Safari and an install in the App Store happen in two universes that don't share state."
            />
            <ProblemCard
              icon={<Smartphone className="size-5" aria-hidden />}
              title="Apple won't tell you who clicked"
              body="iOS exposes no pre-install link context to apps. SKAdNetwork / AdAttributionKit give aggregates only, with a 0–35 day delay."
            />
            <ProblemCard
              icon={<BarChart3 className="size-5" aria-hidden />}
              title="The usual fix is expensive and opaque"
              body="Mainstream MMPs charge per matched install at scale and lean on fingerprinting Apple frowns on. You see headline numbers, not the underlying signal."
            />
          </div>
        </div>
      </section>

      {/* ----- HOW IT WORKS (unified flow) ----- */}
      <section className="border-b">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              From SDK install to creator payout
            </h2>
            <p className="mt-4 text-muted-foreground">
              One round-trip, both sides moving in step.
            </p>
          </div>
          <ol className="relative mt-12 grid gap-6 md:grid-cols-5">
            <Step
              n={1}
              title="App onboards"
              body="Add your bundle IDs and store URLs; drop the thin SDK in. Pulse mints your publishable + secret keys."
            />
            <Step
              n={2}
              title="Campaign published"
              body="Set the bounty and budget; choose open, invite-only, or self-only audience. Tracking URL, short link, and QR are minted on save."
            />
            <Step
              n={3}
              title="Creator claims it"
              body="Creators browse the open shelf and claim what fits their audience. They get their own tracking link tied to the campaign."
            />
            <Step
              n={4}
              title="Click → install → match"
              body="Android via Play Install Referrer; iOS via server-side IP + UA match. Each install is tied back to the creator who drove it."
            />
            <Step
              n={5}
              title="Settle weekly"
              body="App pays only for matched users. Creators cash out once they cross the payout threshold."
            />
          </ol>
        </div>
      </section>

      {/* ----- ATTRIBUTION TECH + DASHBOARD ----- */}
      <section className="border-b">
        <div className="mx-auto grid max-w-6xl gap-12 px-6 py-20 md:grid-cols-2">
          <div>
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Two paths to a match
            </h2>
            <p className="mt-4 text-muted-foreground">
              The right tool for each platform — and no IDFA prompt
              anywhere.
            </p>
            <div className="mt-8 space-y-5">
              <Tech
                icon={<ShieldCheck className="size-5" aria-hidden />}
                title="Android — deterministic"
                body="Google Play Install Referrer API. Effectively 100% accurate. The only miss is sideloaded APKs."
              />
              <Tech
                icon={<Globe2 className="size-5" aria-hidden />}
                title="iOS — server-side matching"
                body="IP + User-Agent matching inside a configurable post-click window. The per-user signal Apple's aggregated reports can't give you."
              />
              <Tech
                icon={<Code2 className="size-5" aria-hidden />}
                title="No ATT prompt, no IDFA"
                body="We don't request IDFA or fire Apple's tracking prompt. Less user friction, cleaner privacy story."
              />
            </div>
          </div>

          <div className="rounded-xl border bg-card p-6 shadow-sm">
            <h3 className="text-lg font-semibold">What lands in your dashboard</h3>
            <ul className="mt-4 space-y-3 text-sm">
              <FeatureLine
                icon={<BarChart3 className="size-4" aria-hidden />}
                text="Daily timeline: clicks, landing clicks, installs, signups"
              />
              <FeatureLine
                icon={<Users className="size-4" aria-hidden />}
                text="Matched users — per-install attribution table"
              />
              <FeatureLine
                icon={<Globe2 className="size-4" aria-hidden />}
                text="Geo, platform, OS, browser, and device-class breakdowns"
              />
              <FeatureLine
                icon={<Activity className="size-4" aria-hidden />}
                text="Funnel drop-offs: Click → Install → Signup → First activity"
              />
              <FeatureLine
                icon={<QrCode className="size-4" aria-hidden />}
                text="Tracking URL, short URL, QR PNG, HTML + Markdown snippets — generated on create"
              />
              <FeatureLine
                icon={<Link2 className="size-4" aria-hidden />}
                text="Referral links: credit a user when their friend installs and signs up"
              />
            </ul>
          </div>
        </div>
      </section>

      {/* ----- USE CASES ----- */}
      <section className="border-b bg-muted/20">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="max-w-2xl">
            <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Channels paid networks don't cover
            </h2>
            <p className="mt-4 text-muted-foreground">
              Concrete scenarios where Pulse is the only place a per-user
              match shows up.
            </p>
          </div>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <UseCase
              icon={<Share2 className="size-5" aria-hidden />}
              title="Influencer drops"
              body="A creator posts to their bio, story, or video description. You see which creator, which post, which install."
            />
            <UseCase
              icon={<QrCode className="size-5" aria-hidden />}
              title="QR codes in the wild"
              body="Posters, packaging, conference booths, podcast art. The scan turns into an attributed install on either platform."
            />
            <UseCase
              icon={<Users className="size-5" aria-hidden />}
              title="In-app referrals"
              body="Existing users invite friends through the SDK. The inviter is credited the moment the friend signs up — iOS and Android."
            />
            <UseCase
              icon={<BarChart3 className="size-5" aria-hidden />}
              title="One channel-mix view"
              body="QR, influencer, referral, partner sites — every non-network channel in a single timeline and funnel."
            />
          </div>
        </div>
      </section>

      {/* ----- FAQ ----- */}
      <section className="border-b">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
            Frequently asked
          </h2>
          <div className="mt-10 divide-y rounded-xl border bg-card shadow-sm">
            <Faq
              q="How heavy is the SDK?"
              a="Tiny. On Android it's a thin wrapper around the official Play Install Referrer client — no background services, no persistent storage. On iOS the SDK only fires a server call at signup; attribution itself runs server-side."
            />
            <Faq
              q="How accurate is iOS attribution without the IDFA?"
              a="Server-side IP + User-Agent matching inside a configurable post-click window. In practice we resolve the large majority of installs that happen within a few hours of the click; long-tail clicks (days later) are intentionally not matched. Always more signal than SKAdNetwork — and per-user, not aggregated."
            />
            <Faq
              q="Can we use this alongside Meta or TikTok ads?"
              a="Yes — Pulse is complementary, not a replacement for ad-network reports. Use the network's own dashboard for paid spend, use Pulse for everything else: QR, influencer, referral, partner sites, organic shares."
            />
            <Faq
              q="Does the user see an ATT prompt?"
              a="No. We don't request IDFA and don't fire Apple's tracking prompt. The privacy story is clean and there's no install friction from a permission dialog."
            />
            <Faq
              q="When do creators get paid?"
              a="Once they cross a configurable payout threshold. Earnings accrue in real time on the dashboard; payouts settle weekly."
            />
            <Faq
              q="Is there a free tier?"
              a="Pulse is free while in beta — no card on file. After that, billing is a flat monthly fee for the app side; we never charge per matched install."
            />
          </div>
        </div>
      </section>

      {/* ----- CTA ----- */}
      <section className="bg-muted/20">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
            See it on your stack
          </h2>
          <p className="mt-4 text-muted-foreground">
            A 20-minute walkthrough with one of our engineers — wired to
            your platforms, your channels, your numbers.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {ready && authenticated ? (
              <Button size="lg" onClick={openApp}>
                Open dashboard
                <ArrowRight />
              </Button>
            ) : (
              <Button size="lg" onClick={() => openDemo('footer-cta')}>
                Request a demo
                <ArrowRight />
              </Button>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-xs text-muted-foreground md:flex-row">
          <div className="flex items-center gap-2">
            <Activity className="size-4 text-primary" aria-hidden />
            <span>Pulse — paid per real install.</span>
          </div>
          <div className="flex items-center gap-4">
            {ready && authenticated ? (
              <button
                type="button"
                onClick={openApp}
                className="hover:text-foreground"
              >
                Open dashboard
              </button>
            ) : (
              <button
                type="button"
                onClick={() => login()}
                className="hover:text-foreground"
              >
                Sign in
              </button>
            )}
          </div>
        </div>
      </footer>

      <RequestDemoDialog
        open={demoOpen}
        onOpenChange={setDemoOpen}
        source={demoSource}
      />
    </div>
  );
}

// ---- small layout helpers -------------------------------------------------

function ProblemCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-6 shadow-sm">
      <div className="mb-3 inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <h3 className="text-base font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="relative rounded-xl border bg-card p-5 shadow-sm">
      <div className="mb-3 inline-flex size-8 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {n}
      </div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </li>
  );
}

function UseCase({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="mb-3 inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function Faq({ q, a }: { q: string; a: string }) {
  return (
    <details className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
      <summary className="flex cursor-pointer items-center justify-between gap-4 text-sm font-medium">
        <span>{q}</span>
        <ChevronDown
          className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden
        />
      </summary>
      <p className="mt-2 text-sm text-muted-foreground">{a}</p>
    </details>
  );
}

function SideCard({
  icon,
  audience,
  title,
  bullets,
  ctaLabel,
  onCta,
  visual,
}: {
  icon: React.ReactNode;
  audience: string;
  title: string;
  bullets: string[];
  ctaLabel: string;
  onCta: () => void;
  visual?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
      {visual && (
        <div className="relative overflow-hidden border-b bg-gradient-to-br from-primary/5 via-primary/0 to-primary/10 p-5">
          {visual}
        </div>
      )}
      <div className="flex flex-1 flex-col p-6">
        <div className="mb-3 inline-flex w-fit items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          {icon}
          {audience}
        </div>
        <h3 className="text-xl font-semibold">{title}</h3>
        <ul className="mt-4 space-y-2 text-sm">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2">
              <CheckCircle2
                className="mt-0.5 size-4 shrink-0 text-primary"
                aria-hidden
              />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <Button variant="outline" onClick={onCta}>
            {ctaLabel}
            <ArrowRight />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Mini product previews used as visuals on the two-sides cards --------

function AppPreview() {
  const sparkPoints = [4, 6, 5, 9, 14, 18, 22];
  const max = Math.max(...sparkPoints);
  const w = 160;
  const h = 36;
  const step = w / (sparkPoints.length - 1);
  const poly = sparkPoints
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * (h - 4) - 2).toFixed(1)}`)
    .join(' ');
  return (
    <div className="rounded-md border bg-background/80 p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-200">
          <span className="inline-block size-1.5 rounded-full bg-emerald-500" />
          Active
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">
          c=k7m3xs
        </span>
      </div>
      <div className="text-sm font-semibold">Spring Sale — IG Story</div>
      <div className="text-xs text-muted-foreground">Acme Shop · Instagram</div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded bg-muted/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground">
            Bounty
          </div>
          <div className="font-semibold tabular-nums">$0.50 / install</div>
        </div>
        <div className="rounded bg-muted/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground">
            Matched
          </div>
          <div className="font-semibold tabular-nums">287 installs</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[10px] uppercase text-muted-foreground">
          7-day
        </span>
        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
          <TrendingUp className="size-3" aria-hidden />
          +18%
        </span>
      </div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="mt-1 h-9 w-full"
        aria-hidden
        preserveAspectRatio="none"
      >
        <polygon
          points={`0,${h} ${poly} ${w},${h}`}
          className="fill-primary/15"
        />
        <polyline
          points={poly}
          fill="none"
          strokeWidth={1.4}
          className="stroke-primary"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

function CreatorPreview() {
  return (
    <div className="rounded-md border bg-background/80 p-4 shadow-sm">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
        Earnings this month
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">$247.50</div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded bg-muted/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground">
            Clicks
          </div>
          <div className="font-semibold tabular-nums">1.4k</div>
        </div>
        <div className="rounded bg-muted/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground">
            Installs
          </div>
          <div className="font-semibold tabular-nums">142</div>
        </div>
        <div className="rounded bg-muted/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground">
            Signups
          </div>
          <div className="font-semibold tabular-nums">68</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5">
        <Link2 className="size-3.5 shrink-0 text-primary" aria-hidden />
        <span className="truncate font-mono text-[11px] text-muted-foreground">
          plsl.app/c/zT3qFxk
        </span>
      </div>
    </div>
  );
}

function Tech({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

function FeatureLine({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
        {icon}
      </span>
      <span>{text}</span>
    </li>
  );
}
